<template>
  <BaseModal :value="true" id="email-order-modal" @input="close">
    <h1 class="modal-title">
      Send order to customer by email
    </h1>
    <div class="modal-body">
      <!-- Left: Customer list -->
      <div class="sidebar" v-if="webCustomers.length > 0 || loadingCustomers">
        <label class="sidebar-label">Linked customers</label>
        <div
          v-if="loadingCustomers"
          class="text-center py-4 text-gray-500 text-sm"
        >
          Loading...
        </div>
        <div v-else class="customer-list">
          <div
            v-for="customer in webCustomers"
            :key="customer.id"
            class="customer-option"
            :class="{ selected: editableEmail === customer.email }"
            @click="selectCustomerEmail(customer)"
          >
            <span class="customer-name"
              >{{ customer.name }} {{ customer.surname }}</span
            >
            <span class="customer-email">{{ customer.email }}</span>
          </div>
        </div>
      </div>

      <!-- Right: Form -->
      <form @submit.prevent="sendOrder" class="email-form">
        <label for="email-input">Email</label>
        <input
          id="email-input"
          type="text"
          class="regular clear bordered"
          v-model="editableEmail"
          @keyup="cleanEmail()"
          required
        />
        <label for="message" class="mt-4">Message for your customer</label>
        <textarea
          id="message"
          class="regular clear bordered h-32"
          v-model="message"
          placeholder="Add extra info for your customer"
        >
        </textarea>
        <div
          class="rounded-md overflow-hidden w-full text-red-700 bg-red-200 p-4 mb-4 my-4"
          v-if="alert"
        >
          {{ alert }}
        </div>
        <button
          type="submit"
          class="btn-success-button w-48 mt-6 mx-auto text-center"
        >
          <template v-if="!loading">
            Send order
          </template>
          <BaseMiniSpinner v-else class="mx-auto" />
        </button>
      </form>
    </div>
  </BaseModal>
</template>
<script>
import { sendOrderToEmail } from "@/api/store/OrdersService.js";
import { getCustomersByInfoautoId } from "@/api/store/CustomersService.js";
export default {
  name: "EmailOrderModal",
  props: {
    orderNumber: {
      type: String,
      required: true
    },
    email: {
      type: String,
      required: false,
      default: ""
    },
    infoautoId: {
      type: String,
      required: false,
      default: ""
    },
    infoautoEmail: {
      type: String,
      required: false,
      default: ""
    },
    infoautoName: {
      type: String,
      required: false,
      default: ""
    }
  },
  data() {
    return {
      editableEmail: "",
      loading: false,
      loadingCustomers: false,
      message: "",
      alert: "",
      webCustomers: []
    };
  },
  mounted() {
    this.editableEmail = this.email;
    this.loadWebCustomers();
  },
  methods: {
    async loadWebCustomers() {
      if (!this.infoautoId) return;
      this.loadingCustomers = true;
      try {
        const res = await getCustomersByInfoautoId(this.infoautoId);
        const customers = Array.isArray(res)
          ? res
          : res?.customers || res?.data || [];
        const webList = customers.filter(c => c.email);

        // Prepend infoauto account email if available and not already in list
        if (this.infoautoEmail) {
          const alreadyInList = webList.some(
            c => c.email.toLowerCase() === this.infoautoEmail.toLowerCase()
          );
          if (!alreadyInList) {
            webList.unshift({
              id: "account",
              name: this.infoautoName,
              surname: "",
              email: this.infoautoEmail
            });
          }
        }

        this.webCustomers = webList;
      } catch {
        this.webCustomers = [];
      } finally {
        this.loadingCustomers = false;
      }
    },
    selectCustomerEmail(customer) {
      this.editableEmail = customer.email;
    },
    validateEmail(email) {
      var re = /\S+@\S+\.\S+/;
      return re.test(email);
    },
    cleanEmail() {
      this.editableEmail = this.editableEmail.replace(/\s/g, "");
    },
    sendOrder() {
      this.alert = "";
      if (!this.validateEmail(this.editableEmail)) {
        this.alert = "email should be valid";
        return;
      }
      this.loading = true;
      sendOrderToEmail(this.orderNumber, this.editableEmail, this.message)
        .then(() => {
          this.$toast.open({
            message: `Order sent to email ${this.editableEmail}`,
            type: "success",
            position: "top-right"
          });
          this.$emit("close");
        })
        .catch(() => {
          this.$toast.open({
            message: `Error sending SMS`,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    close(val) {
      if (!val) {
        this.$emit("close");
      }
    }
  }
};
</script>
<style>
/* Id propio: con el de SMS heredaba su padding-top de 180px (tambien en
   movil), quedaba corrido hacia abajo y el teclado de iOS tapaba el input */
#email-order-modal .modal-content {
  width: 700px;
  height: 80vh;
  max-height: 80vh;
}

/* Movil: pantalla completa y con scroll, para que el input enfocado pueda
   quedar a la vista con el teclado abierto */
@media (max-width: 767px) {
  #email-order-modal.modal {
    padding: 0;
  }

  #email-order-modal .modal-content {
    width: 100%;
    height: 100%;
    max-height: none;
    padding: 12px 12px calc(12px + env(safe-area-inset-bottom));
    border-radius: 0;
    overflow-y: auto;
  }

  #email-order-modal .modal-content > .close {
    top: 4px;
    right: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 0;
    font-size: 32px;
    color: #374151;
  }

  /* Menos de 16px hace que iOS haga zoom al enfocar */
  #email-order-modal input,
  #email-order-modal textarea {
    font-size: 16px;
  }
}
</style>
<style lang="scss" scoped>
.modal-title {
  font-weight: 700;
  text-align: center;
  width: 100%;
  font-size: 18px;
  margin-bottom: 16px;

  /* Que no pase por debajo del cerrar */
  @media (max-width: 767px) {
    padding: 0 44px;
    margin-bottom: 12px;
  }
}
.modal-body {
  display: flex;
  gap: 20px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  @media (max-width: 768px) {
    flex-direction: column;
    overflow: auto;
  }
}
.sidebar {
  flex: 0 0 240px;
  display: flex;
  flex-direction: column;
  @media (max-width: 768px) {
    flex: none;
  }
}
.sidebar-label {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
  color: #374151;
}
.customer-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px;
}
.customer-option {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s;
  &:hover {
    background: #f3f4f6;
  }
  &.selected {
    background: #ecfdf5;
    border-color: #a7f3d0;
  }
  .customer-name {
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
  }
  .customer-email {
    font-size: 12px;
    color: #6b7280;
  }
}
.email-form {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
</style>
